'use client';

import { SelectMenu } from '@app/app/_components/Input/Select/SelectMenu';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { DropdownArrowIcon } from '@app/app/_icons/DropdownArrowIcon';
import { useClickOutside } from '@app/lib/click-outside/useClickOutside';
import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useTranslations } from 'next-intl';
import {
    ReactNode,
    useCallback,
    useRef,
    useState,
    MouseEvent,
    KeyboardEvent,
    ChangeEvent,
    useMemo,
    useEffect,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

interface Props {
    name: string;
    label?: string;
    options: SelectOption[];
    value?: string;
    defaultValue?: string;
    placeholder?: string;
    onChange?: (value: string) => void;
    labelAddon?: ReactNode;
    error?: ViolationError;
    searchable?: boolean;
    onSearch?: (searchQuery: string) => void;
    searchHint?: string;
    noResultsHint?: string;
    bottomAddon?: ReactNode;
    rightIcon?: ReactNode;
    disabled?: boolean;
    noOverlayOnMobile?: boolean;
    labelClassName?: string;
    className?: string;
    autoFocus?: boolean;
}

export function Select({
    name,
    label,
    options,
    value,
    defaultValue,
    placeholder,
    onChange,
    labelAddon,
    error,
    searchable,
    onSearch,
    searchHint,
    noResultsHint,
    bottomAddon,
    rightIcon,
    disabled,
    noOverlayOnMobile = false,
    labelClassName,
    className,
    autoFocus,
}: Props) {
    const selectRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLUListElement>(null);

    const t = useTranslations();

    const isControlled = value !== undefined;

    const [open, setOpen] = useState(false);
    const [internalSelectedOption, setInternalSelectedOption] = useState(() =>
        options.find(option => option.value === defaultValue),
    );
    const [searchQuery, setSearchQuery] = useState('');
    const [showError, handleChange] = useShowErrorState(error, onChange);

    const selectedOption = useMemo(() => {
        if (isControlled) {
            return options.find(option => option.value === value);
        } else {
            return internalSelectedOption;
        }
    }, [isControlled, value, options, internalSelectedOption]);

    useEffect(() => {
        if (!isControlled && defaultValue) {
            const defaultOption = options.find(option => option.value === defaultValue);
            if (defaultOption && !internalSelectedOption) {
                setInternalSelectedOption(defaultOption);
            }
        }
    }, [options, defaultValue, isControlled, internalSelectedOption]);

    const filteredOptions = useMemo(
        () => options.filter(o => o.label.toLowerCase().includes(searchQuery.toLowerCase())),
        [options, searchQuery],
    );

    const handleToggle = useCallback(() => {
        setOpen(isOpen => !isOpen);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setSearchQuery('');
    }, []);

    const handleDismiss = useCallback(() => {
        handleClose();
        selectRef.current?.focus();
    }, [handleClose]);

    const handleKeyDown = useCallback(
        (e: KeyboardEvent<HTMLDivElement>) => {
            if (disabled) {
                e.preventDefault();
                return;
            }

            if (!open && (e.code === 'Space' || e.code === 'Enter')) {
                handleToggle();
                e.preventDefault();
            }

            if (e.code === 'Escape') {
                handleDismiss?.();
            }
        },
        [disabled, handleDismiss, handleToggle, open],
    );

    const handleOptionClick = useCallback(
        (option: SelectOption) => {
            if (!isControlled) {
                setInternalSelectedOption(option);
            }

            handleChange(option.value);
            handleDismiss();
        },
        [isControlled, handleChange, handleDismiss],
    );

    const handleSearchInputClick = useCallback((e: MouseEvent<HTMLInputElement>) => {
        e.stopPropagation();
    }, []);

    const handleSearchChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (onSearch != null) {
                return onSearch(e.target.value);
            }

            setSearchQuery(e.target.value.trim());
        },
        [onSearch],
    );

    useClickOutside(handleClose, [selectRef, menuRef]);

    return (
        <div className={twMerge('flex flex-col gap-0.5', className)}>
            {label != null && (
                <label className={twMerge('mb-1.5 inline-flex items-center gap-1 text-sm', labelClassName)}>
                    {label}
                    {labelAddon != null && labelAddon}
                </label>
            )}

            <input type="hidden" name={name} value={selectedOption?.value ?? ''} />

            <div className="relative flex w-full">
                <div
                    ref={selectRef}
                    className={twJoin(
                        'flex max-w-full flex-grow items-center gap-1',
                        'rounded-md border border-neutral-400 bg-white',
                        'outline-none focus:border-neutral-500 has-[:focus]:border-neutral-500',
                        disabled && 'pointer-events-none opacity-50',
                    )}
                    role="button"
                    onClick={handleToggle}
                    onKeyDown={handleKeyDown}
                    tabIndex={disabled ? -1 : 0}
                    aria-haspopup="listbox"
                    aria-label={`${label} ${selectedOption != null ? selectedOption?.label : ''}`}
                    aria-expanded={open}
                    aria-controls={`select-${name}`}
                    aria-disabled={disabled}>
                    <div
                        className={twJoin(
                            'flex-grow overflow-hidden text-left',
                            selectedOption == null && 'text-black/25',
                        )}>
                        <div
                            className={twJoin(
                                'overflow-hidden text-ellipsis text-nowrap py-2 pl-3',
                                searchable && open && 'block md:hidden',
                            )}>
                            {selectedOption == null ? placeholder : selectedOption.label}
                        </div>
                        {searchable && open && (
                            <input
                                className="hidden w-full bg-transparent py-2 pl-3 text-neutral-700 outline-none md:block"
                                placeholder={t('general.search')}
                                onClick={handleSearchInputClick}
                                onChange={handleSearchChange}
                                type="text"
                                autoFocus={autoFocus}
                            />
                        )}
                    </div>
                    <div className="shrink-0 px-3">
                        {rightIcon == null ? (
                            <DropdownArrowIcon className={twJoin('size-4', open && 'rotate-180')} />
                        ) : (
                            rightIcon
                        )}
                    </div>
                </div>

                {open && (
                    <SelectMenu
                        ref={menuRef}
                        options={filteredOptions}
                        placeholder={placeholder}
                        selectedOption={selectedOption}
                        onOptionClick={handleOptionClick}
                        onDismiss={handleDismiss}
                        noOverlayOnMobile={noOverlayOnMobile}
                        id={`select-${name}`}
                        searchable={searchable}
                        onSearchChange={handleSearchChange}
                        hint={
                            filteredOptions.length === 0 ? (searchQuery === '' ? searchHint : noResultsHint) : undefined
                        }
                        bottomAddon={bottomAddon}
                    />
                )}
            </div>

            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
